import React from 'react'
import isNil from 'lodash/isNil'
import {
  GrantOutcome,
  GrantProgress,
  GrantRowDetail,
  GrantRowEmail,
  GrantRowList,
  GrantRowMain,
  GrantScroll,
  GrantSplitRow,
  GrantSummary,
  InviteModalForm,
} from './UserManagement.styles'
import { pluralize } from './userDisplay'

export interface AccessOutcomeStyle<TAction extends string> {
  action: TAction
  /** Badge on the row. */
  label: string
  /** How the outcome is named in the headline, e.g. "invited". */
  summaryLabel: string
  tone: 'success' | 'notice' | 'error'
}

interface AccessResultItem<TAction extends string> {
  email: string
  action: TAction
  reason?: string
}

interface AccessResultListProps<TAction extends string> {
  items: AccessResultItem<TAction>[]
  /** Outcomes in the order the headline should name them. */
  outcomes: AccessOutcomeStyle<TAction>[]
  /**
   * Company names per address. A run spans several companies, so which one an
   * outcome landed on is the one thing the address alone cannot say.
   */
  companiesByEmail?: Record<string, string[]>
  /** In-flight line, shown while a retry is running over an existing report. */
  progress?: { processed: number; total: number } | null
  progressLabel?: string
}

/**
 * A row's companies. Some managers administer thirty of them, so past a couple
 * the names move to the hover title, as they do on the confirm step.
 */
const renderCompanies = (names: string[] | undefined) => {
  if (isNil(names) || names.length === 0) return null
  const title = names.join(', ')
  return (
    <GrantRowDetail title={title}>
      {names.length > 2 ? `${names.length} companies` : title}
    </GrantRowDetail>
  )
}

/**
 * Per-recipient outcome of a bulk access run, shared by the grant and remove
 * modals. The headline names only the outcomes that actually happened, so it
 * never claims a category with no rows.
 */
const AccessResultList = <TAction extends string>({
  items,
  outcomes,
  companiesByEmail,
  progress = null,
  progressLabel = 'Processed',
}: AccessResultListProps<TAction>) => {
  const styleByAction = new Map(
    outcomes.map((outcome) => [outcome.action, outcome])
  )

  const summary = outcomes
    .map((outcome) => ({
      count: items.filter((item) => item.action === outcome.action).length,
      label: outcome.summaryLabel,
    }))
    .filter(({ count }) => count > 0)
    .map(({ count, label }) => `${count} ${label}`)
    .join(', ')

  return (
    <InviteModalForm>
      <GrantSummary>
        {pluralize(items.length, 'recipient')} processed: {summary}.
      </GrantSummary>
      <GrantScroll>
        <GrantRowList>
          {items.map((item) => {
            const outcome = styleByAction.get(item.action)
            return (
              <GrantSplitRow key={item.email}>
                <GrantRowMain>
                  <GrantRowEmail>{item.email}</GrantRowEmail>
                  {renderCompanies(companiesByEmail?.[item.email])}
                  {item.reason ? (
                    <GrantRowDetail>{item.reason}</GrantRowDetail>
                  ) : null}
                </GrantRowMain>
                <GrantOutcome $tone={outcome?.tone ?? 'notice'}>
                  {outcome?.label ?? item.action}
                </GrantOutcome>
              </GrantSplitRow>
            )
          })}
        </GrantRowList>
      </GrantScroll>
      {progress ? (
        <GrantProgress>
          {progressLabel} {progress.processed} of {progress.total}…
        </GrantProgress>
      ) : null}
    </InviteModalForm>
  )
}

export { AccessResultList }
